import { prettyPrintDateTimeMed } from "util/dates";

/** One person's email, as a line of text; `problem` marks the ones that need attention. */
export type EmailStatus = { text: string; problem: boolean };

type RecipientDelivery = {
  inApp?: boolean;
  inAppSuppressedBy?: string | null;
  readAt?: string | null;
  emailMode?: string | null;
  emailSuppressedBy?: string | null;
  emailOutcome?: string | null;
  emailReason?: string | null;
  emailHeldReason?: string | null;
  emailAttemptedAt?: string | null;
  emailAttempts?: number;
};

const INCLUSION_REASONS: Record<string, string> = {
  SUBSCRIBER: "Subscriber",
  ASSIGNEE: "Assignee",
  WATCHER: "Watcher",
  REVIEWER: "Reviewer",
  SIGNER: "Signer",
  PARTICIPANT: "Participant",
  REQUESTER: "Requested it",
  FACILITY_AUDIENCE: "Facility access",
  STAFF_COMPOSED: "Queued by staff",
  STAFF_TEST: "Test notification",
  ADDED_LATER: "Added after it was sent",
};

const EXCLUSION_REASONS: Record<string, string> = {
  DEACTIVATED: "Deactivated",
  NOT_INVITED: "Never invited",
  ORGANIZATION_DISABLED: "Organization is disabled",
  NOT_SUBSCRIBED: "Not subscribed to this organization",
  NOT_IN_ORGANIZATION: "Not a member of this organization",
  ALL_CHANNELS_OFF: "Every channel turned off",
  NO_INBOX: "No inbox (org has notifications off)",
};

const SUPPRESSION_SOURCES: Record<string, string> = {
  USER: "turned off by them for this organization",
  EVERYWHERE: "turned off by them everywhere",
  TENANT: "turned off by the organization",
  DEFAULT: "off by default",
  KIND_POLICY: "not offered for this alert",
  STAFF_PAUSED: "paused for all staff",
  IN_APP_ONLY: "this notification is in-app only",
  NO_INBOX: "no inbox: their organization has notifications off",
};

/** Unknown values still read as words, so a new code shows up rather than vanishing. */
function humanize(value: string): string {
  const words = value.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function inclusionReasonLabel(reason: string): string {
  return INCLUSION_REASONS[reason] ?? humanize(reason);
}

export function exclusionReasonLabel(reason: string): string {
  return EXCLUSION_REASONS[reason] ?? humanize(reason);
}

export function suppressionLabel(source: string | null | undefined): string | null {
  return source ? (SUPPRESSION_SOURCES[source] ?? humanize(source).toLowerCase()) : null;
}

function withParenthetical(text: string, note: string | null | undefined): string {
  return note ? `${text} (${note})` : text;
}

/** "read Oct 14, 9:02 AM", "unread", or "off (turned off by the organization)". */
export function inAppText(recipient: RecipientDelivery): string {
  if (!recipient.inApp) {
    return withParenthetical("off", suppressionLabel(recipient.inAppSuppressedBy));
  }
  return recipient.readAt ? `read ${prettyPrintDateTimeMed(recipient.readAt)}` : "unread";
}

/** When the email outcome happened, and how many tries it took when that was more than one. */
function attemptSuffix(recipient: RecipientDelivery): string {
  const when = recipient.emailAttemptedAt
    ? ` ${prettyPrintDateTimeMed(recipient.emailAttemptedAt)}`
    : "";
  const attempts = recipient.emailAttempts ?? 0;
  return attempts > 1 ? `${when} after ${attempts} attempts` : when;
}

/**
 * An email with no outcome yet: why it's held, and the last try's error when a send
 * failed and will be retried, which needs attention.
 */
function waitingStatus(text: string, recipient: RecipientDelivery): EmailStatus {
  const held = recipient.emailHeldReason ? `held: ${recipient.emailHeldReason}` : null;
  const lastTry = recipient.emailReason ? `last try failed: ${recipient.emailReason}` : null;
  const notes = [held, lastTry].filter((note) => note !== null).join("; ");
  return { text: withParenthetical(text, notes), problem: lastTry !== null };
}

/**
 * `byLegacyDigest`: the organization's legacy digest still mails this alert, so a digest
 * row with no outcome is that digest's to send, and whether it did isn't recorded here.
 */
export function emailStatus(recipient: RecipientDelivery, byLegacyDigest = false): EmailStatus {
  const reason = recipient.emailReason;
  const suffix = attemptSuffix(recipient);
  switch (recipient.emailOutcome) {
    case "SENT": {
      const sent = recipient.emailMode === "DIGEST" ? "sent in digest" : "sent";
      return { text: withParenthetical(`${sent}${suffix}`, reason), problem: false };
    }
    case "FAILED":
      return { text: `failed${suffix}${reason ? `: ${reason}` : ""}`, problem: true };
    case "SKIPPED":
      return { text: withParenthetical("skipped", reason), problem: false };
    case "UNCONFIRMED":
      return { text: withParenthetical(`unconfirmed${suffix}`, reason), problem: true };
  }
  switch (recipient.emailMode) {
    case "NONE":
      return {
        text: withParenthetical("none", suppressionLabel(recipient.emailSuppressedBy)),
        problem: false,
      };
    case "DIGEST":
      return byLegacyDigest
        ? { text: "handled by the older digest (not tracked here)", problem: false }
        : waitingStatus("waiting for digest", recipient);
    case "IMMEDIATE":
      return waitingStatus("pending", recipient);
    default:
      return { text: "not recorded", problem: false };
  }
}

/** A word or two for why a channel was off, for a table cell; the full words are `suppressionLabel`. */
const SHORT_SUPPRESSION_SOURCES: Record<string, string> = {
  USER: "Muted",
  EVERYWHERE: "Muted",
  TENANT: "Org muted",
  DEFAULT: "Off by default",
  KIND_POLICY: "Not offered",
  STAFF_PAUSED: "Paused",
  IN_APP_ONLY: "In-app only",
  NO_INBOX: "No inbox",
};

function shortSuppression(source: string | null | undefined): string {
  return source ? (SHORT_SUPPRESSION_SOURCES[source] ?? humanize(source)) : "Off";
}

/** A chip's worth of someone's in-app state; the full line is `inAppText`. */
export type InAppChip = { label: string; tone: "success" | "warning" | "default" };

export function inAppChip(recipient: RecipientDelivery): InAppChip {
  if (!recipient.inApp) {
    return { label: shortSuppression(recipient.inAppSuppressedBy), tone: "default" };
  }
  return recipient.readAt
    ? { label: "Read", tone: "success" }
    : { label: "Unread", tone: "warning" };
}

/** A table cell's worth of `emailStatus`, flagged the same way. */
export function emailShortStatus(
  recipient: RecipientDelivery,
  byLegacyDigest = false
): EmailStatus {
  const { problem } = emailStatus(recipient, byLegacyDigest);
  const digest = recipient.emailMode === "DIGEST";
  switch (recipient.emailOutcome) {
    case "SENT":
      return { text: digest ? "Sent in digest" : "Sent", problem };
    case "FAILED":
      return { text: "Failed", problem };
    case "SKIPPED":
      return { text: "Skipped", problem };
    case "UNCONFIRMED":
      return { text: "Unconfirmed", problem };
  }
  if (recipient.emailMode === "NONE") {
    return { text: shortSuppression(recipient.emailSuppressedBy), problem };
  }
  if (digest) {
    return { text: byLegacyDigest ? "Older digest" : "Waiting for digest", problem };
  }
  return { text: recipient.emailMode === "IMMEDIATE" ? "Pending" : "Not recorded", problem };
}
